/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState, type KeyboardEvent } from 'react';

import { css, jsx } from '@compiled/react';
import { defineMessages, FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import Modal, { ModalBody, ModalHeader, ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Flex, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import chartIconDarkSvg from './assets/darkModeIcons/chart-icon-dark.svg';
import diagramIconDarkSvg from './assets/darkModeIcons/diagram-icon-dark.svg';
import infographicIconDarkSvg from './assets/darkModeIcons/infographic-dark.svg';
import visualAidIconDarkSvg from './assets/darkModeIcons/visual-aid-dark.svg';
import chartIconSvg from './assets/lightModeIcons/chart-icon.svg';
import diagramIconSvg from './assets/lightModeIcons/diagram-icon.svg';
import infographicIconSvg from './assets/lightModeIcons/infographic-light.svg';
import visualAidIconSvg from './assets/lightModeIcons/visual-aid.svg';
import remixIllustrationSvg from './assets/Remix-Illustration.svg';

const messages = defineMessages({
	heading: {
		id: 'malleable-ui-components.remix.heading',
		defaultMessage: 'Remix',
		description: 'Heading displayed at the top of the Remix overlay panel',
	},
	subtitle: {
		id: 'malleable-ui-components.remix.subtitle',
		defaultMessage: 'Visualize this content in a new way',
		description: 'Subtitle below the Create a remix heading',
	},
	labsLozenge: {
		id: 'malleable-ui-components.remix.labs-lozenge',
		defaultMessage: 'LABS',
		description: 'Label for the LABS lozenge in the Remix overlay header',
	},
	selectedContent: {
		id: 'malleable-ui-components.remix.selected-content',
		defaultMessage: 'Selected content',
		description: 'Label shown above the displayed selected text content in the Remix overlay',
	},
	noContent: {
		id: 'malleable-ui-components.remix.no-content',
		defaultMessage: 'No content selected',
		description: 'Fallback message displayed when no text content is currently selected',
	},
	remixOptionChart: {
		id: 'malleable-ui-components.remix.option.chart',
		defaultMessage: 'Chart',
		description: 'Label for the Chart remix option',
	},
	remixOptionDiagram: {
		id: 'malleable-ui-components.remix.option.diagram',
		defaultMessage: 'Diagram',
		description: 'Label for the Diagram remix option',
	},
	remixOptionInfographic: {
		id: 'malleable-ui-components.remix.option.infographic',
		defaultMessage: 'Infographic',
		description: 'Label for the Infographic remix option',
	},
	remixOptionVisualization: {
		id: 'malleable-ui-components.remix.option.visualization',
		defaultMessage: 'Visualization',
		description: 'Label for the Visualization remix option',
	},
	describePlaceholder: {
		id: 'malleable-ui-components.remix.describe-placeholder',
		defaultMessage: 'Or describe your remix...',
		description: 'Placeholder for the remix description input field',
	},
	describeSubmitLabel: {
		id: 'malleable-ui-components.remix.describe-submit-label',
		defaultMessage: 'Submit',
		description: 'Accessible label for the describe input submit button',
	},
	remixIconAlt: {
		id: 'malleable-ui-components.remix.icon-alt',
		defaultMessage: 'Remix icon',
		description: 'Alt text for the decorative remix gradient icon in the overlay header',
	},
	chartIconAlt: {
		id: 'malleable-ui-components.remix.option.chart-icon-alt',
		defaultMessage: 'Chart icon',
		description: 'Alt text for the chart option icon',
	},
	diagramIconAlt: {
		id: 'malleable-ui-components.remix.option.diagram-icon-alt',
		defaultMessage: 'Diagram icon',
		description: 'Alt text for the diagram option icon',
	},
	infographicIconAlt: {
		id: 'malleable-ui-components.remix.option.infographic-icon-alt',
		defaultMessage: 'Infographic icon',
		description: 'Alt text for the infographic option icon',
	},
	visualizationIconAlt: {
		id: 'malleable-ui-components.remix.option.visualization-icon-alt',
		defaultMessage: 'Visualization icon',
		description: 'Alt text for the visualization option icon',
	},
	chartTooltip: {
		id: 'malleable-ui-components.remix.option.chart-tooltip',
		defaultMessage: 'View your data as a chart or graph',
		description: 'Tooltip for the Chart remix option',
	},
	diagramTooltip: {
		id: 'malleable-ui-components.remix.option.diagram-tooltip',
		defaultMessage: 'Show relationships with a diagram',
		description: 'Tooltip for the Diagram remix option',
	},
	infographicTooltip: {
		id: 'malleable-ui-components.remix.option.infographic-tooltip',
		defaultMessage: 'Summarize key points with an infographic',
		description: 'Tooltip for the Infographic remix option',
	},
	visualizationTooltip: {
		id: 'malleable-ui-components.remix.option.visualization-tooltip',
		defaultMessage: 'Bring your content to life with a bespoke visual',
		description: 'Tooltip for the Visualization remix option',
	},
	labsLozengeTooltip: {
		id: 'malleable-ui-components.remix.option.lozenge-tooltip',
		defaultMessage: 'Beta: This feature is still in development.',
		description: 'Tooltip for the LABS lozenge tag',
	},
});

const modalContentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	backgroundColor: token('elevation.surface.sunken'),
	borderRadius: 'inherit',
});

// Position the Labs lozenge 16px from the top-right corner of the modal.
// Using calc() to negate the token value since we need negative offset
// to compensate for the ModalHeader's 24px padding (24px - 8px = 16px from edge)
const labsRowPositionStyle = css({
	position: 'absolute',
	top: `calc(-1 * ${token('space.100')})`,
	right: `calc(-1 * ${token('space.100')})`,
});

const remixStyles = cssMap({
	headerRow: {
		width: '100%',
		position: 'relative',
	},
	labsRow: {
		cursor: 'default',
		width: 'auto',
	},
	remixIconWrapper: {
		position: 'relative',
		width: '64px',
		height: '64px',
	},
	subtitle: {
		color: token('color.text.subtlest'),
	},
	describeInputBorderWrapper: {
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	describeInputWrapper: {
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		backgroundColor: token('elevation.surface.sunken'),
	},
	describePromptRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
		width: '100%',
	},
	describeTextAreaWrapper: {
		flex: '1 1 auto',
		minWidth: '0px',
	},
	modalBodyPadding: {
		paddingInline: token('space.200'),
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.200'),
	},
	remixOptionsStack: {
		width: '100%',
	},
	remixOptionPressable: {
		width: '100%',
		height: '48px',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: token('elevation.surface.overlay'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.100'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	remixOptionIconContainer: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.tile'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
	},
});

const remixIconLayerStyle = css({
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	objectFit: 'cover',
});

const RemixOptionIcon = ({ src, srcDark, alt }: { src: string; srcDark: string; alt: string }) => (
	<Box as="span" xcss={remixStyles.remixOptionIconContainer}>
		<Image src={src} srcDark={srcDark} alt={alt} />
	</Box>
);

export type RemixOptionKind = 'chart' | 'diagram' | 'infographic' | 'visual-aid' | 'custom';
export type RemixOptionSelectionParameters = {
	type: RemixOptionKind;
	userPrompt?: string;
};

type RemixOption = {
	type: RemixOptionKind;
	icon: string;
	iconDark: string;
	labelMessage: MessageDescriptor;
	iconAltMessage: MessageDescriptor;
	tooltipMessage: MessageDescriptor;
	testId: string;
};

const REMIX_OPTIONS: RemixOption[] = [
	{
		type: 'chart',
		icon: chartIconSvg,
		iconDark: chartIconDarkSvg,
		labelMessage: messages.remixOptionChart,
		iconAltMessage: messages.chartIconAlt,
		tooltipMessage: messages.chartTooltip,
		testId: 'remix-overlay-option-chart',
	},
	{
		type: 'diagram',
		icon: diagramIconSvg,
		iconDark: diagramIconDarkSvg,
		labelMessage: messages.remixOptionDiagram,
		iconAltMessage: messages.diagramIconAlt,
		tooltipMessage: messages.diagramTooltip,
		testId: 'remix-overlay-option-diagram',
	},
	{
		type: 'infographic',
		icon: infographicIconSvg,
		iconDark: infographicIconDarkSvg,
		labelMessage: messages.remixOptionInfographic,
		iconAltMessage: messages.infographicIconAlt,
		tooltipMessage: messages.infographicTooltip,
		testId: 'remix-overlay-option-infographic',
	},
	{
		type: 'visual-aid',
		icon: visualAidIconSvg,
		iconDark: visualAidIconDarkSvg,
		labelMessage: messages.remixOptionVisualization,
		iconAltMessage: messages.visualizationIconAlt,
		tooltipMessage: messages.visualizationTooltip,
		testId: 'remix-overlay-option-visual-aid',
	},
];

/**
 * Props for the Remix overlay modal.
 */
interface RemixOverlayProps {
	/** Called when the user closes the overlay (e.g. escape or overlay click). */
	onClose: () => void;
	/** Optional callback when the user selects a remix option (chart, diagram, infographic, visual). */
	onOptionSelect?: (params: RemixOptionSelectionParameters) => void;
}

/**
 * A centered modal overlay that displays the currently selected content.
 * Used by the Remix feature in both the floating toolbar and block menu.
 */
export function RemixOverlay({ onClose, onOptionSelect }: RemixOverlayProps) {
	const { formatMessage } = useIntl();
	const [describeValue, setDescribeValue] = useState('');

	const handleDescribeSubmit = useCallback(() => {
		if (describeValue?.trim()) {
			onOptionSelect?.({ type: 'custom', userPrompt: describeValue });
		}
	}, [describeValue, onOptionSelect]);

	const handleDescribeKeyDown = useCallback(
		(e: KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleDescribeSubmit();
			}
		},
		[handleDescribeSubmit],
	);

	return (
		<ModalTransition>
			<Modal onClose={onClose} width={320} testId="remix-overlay">
				<div css={modalContentWrapperStyle}>
					<ModalHeader hasCloseButton={false}>
						<Box xcss={remixStyles.headerRow}>
							<Stack space="space.100" alignInline="center">
								<Tooltip content={formatMessage(messages.labsLozengeTooltip)} position="top">
									{(tooltipProps) => (
										<div css={labsRowPositionStyle}>
											<Flex {...tooltipProps} justifyContent="end" xcss={remixStyles.labsRow}>
												<Lozenge appearance="new">
													<FormattedMessage {...messages.labsLozenge} />
												</Lozenge>
											</Flex>
										</div>
									)}
								</Tooltip>
								<Flex
									xcss={remixStyles.remixIconWrapper}
									aria-hidden
									alignItems="center"
									justifyContent="center"
								>
									<Image
										src={remixIllustrationSvg}
										alt={formatMessage(messages.remixIconAlt)}
										css={remixIconLayerStyle}
									/>
								</Flex>
								<Stack space="space.050" alignInline="center">
									<Heading size="large">
										<FormattedMessage {...messages.heading} />
									</Heading>
									<Box xcss={remixStyles.subtitle}>
										<Text size="small" color="color.text.subtle">
											<FormattedMessage {...messages.subtitle} />
										</Text>
									</Box>
								</Stack>
							</Stack>
						</Box>
					</ModalHeader>
					<ModalBody hasInlinePadding={false}>
						<Box xcss={remixStyles.modalBodyPadding}>
							<Stack space="space.100" alignInline="stretch" xcss={remixStyles.remixOptionsStack}>
								{REMIX_OPTIONS.map((option) => (
									<Tooltip
										key={option.type}
										content={formatMessage(option.tooltipMessage)}
										position="mouse"
										mousePosition="right"
									>
										{(tooltipProps) => (
											<Pressable
												{...tooltipProps}
												xcss={remixStyles.remixOptionPressable}
												onClick={() => onOptionSelect?.({ type: option.type })}
												testId={option.testId}
											>
												<Flex alignItems="center" gap="space.100">
													<RemixOptionIcon
														src={option.icon}
														srcDark={option.iconDark}
														alt={formatMessage(option.iconAltMessage)}
													/>
													<Text>{formatMessage(option.labelMessage)}</Text>
												</Flex>
											</Pressable>
										)}
									</Tooltip>
								))}
							</Stack>
						</Box>
						<Box xcss={remixStyles.describeInputBorderWrapper}>
							<Box xcss={remixStyles.describeInputWrapper}>
								<Form onSubmit={handleDescribeSubmit}>
									<Flex xcss={remixStyles.describePromptRow}>
										<Box xcss={remixStyles.describeTextAreaWrapper}>
											<TextArea
												appearance="none"
												placeholder={formatMessage(messages.describePlaceholder)}
												value={describeValue}
												onChange={(e) => setDescribeValue(e.currentTarget.value)}
												onKeyDown={handleDescribeKeyDown}
												isCompact
												minimumRows={1}
												maxHeight="200px"
												resize="smart"
												aria-label={formatMessage(messages.describePlaceholder)}
												testId="remix-overlay-describe-input"
												name="remix-overlay-describe"
											/>
										</Box>
										<IconButton
											type="submit"
											appearance="primary"
											isDisabled={!describeValue?.trim()}
											icon={ArrowUpIcon}
											label={formatMessage(messages.describeSubmitLabel)}
											testId="remix-overlay-describe-submit"
										/>
									</Flex>
								</Form>
							</Box>
						</Box>
					</ModalBody>
				</div>
			</Modal>
		</ModalTransition>
	);
}
